import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import CONTENT from "../../seo-content.json";
import { EXTRA_BLOCKS } from "../../mock";

export const ROUTES_BY_SLUG = Object.fromEntries(CONTENT.routes.map((r) => [r.slug, r]));

const useFaqJsonLd = (faqs) => {
  useEffect(() => {
    if (!faqs?.length) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "ld-faq";
    el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    document.getElementById("ld-faq")?.remove();
    document.head.appendChild(el);
    return () => el.remove();
  }, [faqs]);
};

const RouteList = ({ slugs }) => (
  <div className="mb-10 sm:mb-14" data-testid="route-links">
    <h2 className="font-serif-d text-[22px] sm:text-[28px] font-semibold text-[#183C35]">Popular routes &amp; fixed prices</h2>
    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
      {slugs.map((s) => { const r = ROUTES_BY_SLUG[s]; return (
        <Link key={s} to={`/${s}`} className="group flex items-center justify-between gap-3 rounded-xl bg-white border border-[#183C35]/10 px-4 py-3 hover:border-[#3E7F8E]/40 transition-colors" data-testid={`route-link-${s}`}>
          <span className="text-[14.5px] text-[#183C35]">{r.from} to {r.to}<span className="block text-[12.5px] text-[#202522]/55">~{r.minutes} min · {r.km} km</span></span>
          <span className="shrink-0 text-[14px] font-semibold text-[#3E7F8E] inline-flex items-center gap-1">from €{r.prices[0]}<ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></span>
        </Link>
      ); })}
    </div>
  </div>
);

export const FaqList = ({ faqs }) => (
  <div data-testid="faq-section">
    <h2 className="font-serif-d text-[22px] sm:text-[28px] font-semibold text-[#183C35]">Frequently asked questions</h2>
    <Accordion type="single" collapsible className="mt-3 bg-white rounded-2xl border border-[#183C35]/10 px-4 sm:px-6">
      {faqs.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`} className={i === faqs.length - 1 ? "border-b-0" : ""}>
          <AccordionTrigger className="text-left text-[15px] sm:text-[16px] text-[#183C35] hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-[14.5px] leading-relaxed text-[#202522]/70">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

// Extra text blocks added from Admin → Website texts.
export const ExtraBlocks = ({ page }) => {
  const blocks = EXTRA_BLOCKS[page] || [];
  if (!blocks.length) return null;
  return (
    <div className="space-y-5 mb-10" data-testid={`extra-blocks-${page}`}>
      {blocks.map((b, i) => (
        <div key={i}>
          {b.heading && <h2 className="font-serif-d text-[22px] sm:text-[26px] font-semibold text-[#183C35]">{b.heading}</h2>}
          {b.text && <p className="mt-2 text-[15px] leading-relaxed text-[#202522]/75 whitespace-pre-line">{b.text}</p>}
        </div>
      ))}
    </div>
  );
};

// Bottom-of-page block: routes, FAQs and a discreet "About this service" note.
export const SeoExtras = ({ path, page }) => {
  const faqs = CONTENT.faqs[path];
  const about = EXTRA_BLOCKS.__about?.[path] ?? CONTENT.about[path];
  const routes = CONTENT.routeLinks[path];
  useFaqJsonLd(faqs);
  if (!faqs && !about && !routes && !CONTENT.extraText?.[path] && !(EXTRA_BLOCKS[page] || []).length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 pt-10 sm:pt-16 pb-6" data-testid="seo-extras">
      <ExtraBlocks page={page} />
      {CONTENT.extraText?.[path] && <p className="mb-8 sm:mb-10 max-w-4xl text-[15px] sm:text-[16px] leading-relaxed text-[#202522]/75" data-testid="extra-text">{CONTENT.extraText[path]}</p>}
      {routes && <RouteList slugs={routes} />}
      {faqs && <FaqList faqs={faqs} />}
      {about && (
        <div className="mt-8 sm:mt-10 pt-5 border-t border-[#183C35]/10 max-w-4xl" data-testid="about-service">
          <p className="text-[11.5px] uppercase tracking-[0.12em] text-[#3E7F8E]">About this service</p>
          <p className="mt-1.5 text-[13px] sm:text-[13.5px] leading-relaxed text-[#202522]/55">{about}</p>
        </div>
      )}
    </section>
  );
};
